import { Alert, Heading } from '@mm/components/src';
import {
  activeChatbotSelector,
  chatbotIsActiveOnConnectedPageSelector,
} from '@mm/state/selectors/viewSelectors';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { StoreState } from '@mm/state/store-interfaces';
import { selectChatStarterRecordsByUiFilterKind } from '@mm/state/selectors/chatStarters';
import { selectInstagramLive } from '@mm/state/selectors/promoterSelector';
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import css from './InstagramLive.module.css';
import { InactiveChannelAlertSmart } from '../../campaigns/panels/DripCampaignDetailsPanel/InactiveChannelAlert';
import { EditStartBotHeader } from '../common/StartBotPromoterPanel/EditStartBotHeader/EditStartBotHeader';
import { useEditPagePrimeState } from '../../../common/hooks/use-edit-page-prime-state';
import {
  PathGraphContext,
  useNewGraphInstance,
} from '../../../../common/widgets/common/PathGraph/PathGraph';
import { StartBotsDialogueEditor } from '../common/StartBotsDialogueEditor/StartBotsDialogueEditor';
import { BotBuilderRoutes } from '../../../../routes/bot-builder-routes';
import { DialogueSelectorWithReload } from '../common/DialogueSelectorWithReload/DialogueSelectorWithReload';
import { useSupportedWidgets } from '../../../../common/components/AddWidgetInsert/AddWidgetInsert';
import { IG_SUPPORTED_WIDGET_TYPES } from '../instagramStoryMention/InstagramStoryMentionEditor';
import Spinner from '../../../../common/components/Spinner';
import { DialoguePreview } from '../../../../modules/DailoguePreview/DialoguePreview';
import FrequencyCapControl from '../commentGuards/components/CommentGuardForm/FrequencyCapControl';
import { useCatchAllConfirmModal } from '../commentGuards/use-catch-all-confirm-modal';
import { useShowInactiveBannerFlag } from '../../../common/hooks/use-show-inactive-bot-banner-flag';
import { InstagramRespondRadioBtns } from '../../common/components/IGRespondRadioBtns/InstagramRespondRadioBtns';
import { useResolutionInfo } from '@mm/hooks';

type Props = {
  className?: string;
};

export const InstagramLive = ({ className }: Props) => {
  const { widgets, page, promoter } = useEditPagePrimeState(
    'instagram_live_comment_guard',
  );
  const [promoters, setPromoters] = useState<PromoterModel[]>([]);
  const [isRemoteCatchAllIgLiveUpdated, setIsRemoteCatchAllIgLiveUpdated] =
    useState(false);
  const instagramPromoters = useSelector(selectInstagramLive);
  const chatbot = useSelector(activeChatbotSelector);
  const pathGraph = useNewGraphInstance(promoter?.pageId);
  const chatbotIsActiveOnConnectedPage = useSelector(
    chatbotIsActiveOnConnectedPageSelector,
  );
  const { onClickActivate } = useShowInactiveBannerFlag();
  const { openActivateInactiveBotModal } = useModalActions();
  const { allWidgets: allSupportedWidgets } = useSupportedWidgets();
  const { screenWidth } = useResolutionInfo();
  const widgetsToExclude = allSupportedWidgets
    .filter(w => !IG_SUPPORTED_WIDGET_TYPES.includes(w.type))
    .map(w => w.type);

  const instagramLiveStarters = useSelector((state: StoreState) =>
    selectChatStarterRecordsByUiFilterKind(
      state,
      'instagram_live_comment_guard',
    ),
  );
  const promoterCRUDActions = usePromoterCRUDActions();

  useEffect(() => {
    const fetchPromoters = async () => {
      const response = await promoterCRUDActions.retrieveAll(
        chatbot.id,
        'instagram_live_comment_guard',
      );
      setPromoters(response);
    };
    fetchPromoters();
  }, []);

  const remoteCatchAllIgLive = instagramLiveStarters?.chatStarters?.find(
    starter =>
      starter.subKind === 'all' &&
      starter.active &&
      (starter.postId === 'ALL' || !starter.postId),
  );

  const inactiveAllIgLive =
    (promoter?.postId === 'ALL' || !promoter?.postId) &&
    !promoter?.keywordsEnabled &&
    !promoter?.active;

  const showCatchAllModal =
    remoteCatchAllIgLive &&
    remoteCatchAllIgLive.id !== promoter?.id &&
    inactiveAllIgLive;

  const openCatchAllConfirmModal = useCatchAllConfirmModal(
    'Important Note',
    `Activating this catch-all Instagram Live will disable ${remoteCatchAllIgLive?.name}
    Are you sure you would like to proceed?`,
  );

  const setCatchAll = () => {
    if (remoteCatchAllIgLive) {
      const instagramCatchAllPromoterToDeactivate = instagramPromoters?.find(
        promoter => promoter.id === remoteCatchAllIgLive.id && promoter.active,
      );

      if (instagramCatchAllPromoterToDeactivate) {
        promoterCRUDActions.update(chatbot.id, {
          ...instagramCatchAllPromoterToDeactivate,
          active: false,
        });
      }
    }
  };
  const updatePromoter = () => {
    if (showCatchAllModal) {
      setCatchAll();
    }

    if (promoter) {
      promoterCRUDActions.update(chatbot.id, {
        ...promoter,
        active: !promoter.active,
      });
    }
  };

  const onModalClose = (ok: boolean) => {
    if (ok) {
      setCatchAll();
      setIsRemoteCatchAllIgLiveUpdated(true);
    }
  };
  const customActivateAction = () => {
    if (promoter) {
      if (!chatbotIsActiveOnConnectedPage && !promoter.active) {
        openActivateInactiveBotModal({
          onBotActivateConfirm: () =>
            setTimeout(() => onClickActivate(updatePromoter), 500),
          useCustomOnBotActivateConfirm: true,
        });
      } else if (showCatchAllModal && !isRemoteCatchAllIgLiveUpdated) {
        openCatchAllConfirmModal(updatePromoter);
      } else {
        updatePromoter();
      }
    }
  };

  const dialogueSelectorDropdown = useMemo(() => {
    const promoterDialogueUpdate = async (dialogueId: number) => {
      if (promoter && promoter.id) {
        const updatedPromoter = {
          ...promoter,
          pageId: dialogueId,
        };
        const savedPromoter = await promoterCRUDActions.update(
          chatbot?.id,
          updatedPromoter,
        );

        const newDialogueId = savedPromoter?.pageId;

        if (newDialogueId) {
          const newUrl = BotBuilderRoutes.instagramLiveEdit.getLink({
            chatbotId: chatbot?.id,
            dialogueId: newDialogueId,
            promoterId: promoter.id,
          });
          return {
            newDialogueId,
            newUrl,
          };
        }
      }
    };

    return promoter?.pageId ? (
      <DialogueSelectorWithReload
        dialogueId={promoter.pageId}
        entityUpdateFunction={promoterDialogueUpdate}
      />
    ) : null;
  }, [promoter, chatbot?.id]);

  if (!promoter?.id) {
    return (
      <div className={classNames(className, css.container)}>
        <Spinner standalone size={24} />
      </div>
    );
  }

  return (
    <div className={classNames(className, css.container)}>
      <InactiveChannelAlertSmart instagram type="Live" />
      {page && (
        <EditStartBotHeader
          promoter={promoter}
          page={page}
          botIsTemplate={!!chatbot.template}
          customActivateAction={customActivateAction}
        />
      )}
      <Alert type="warning" showCloseIcon={false}>
        Сomments on live video will only be sent while video being broadcast.
      </Alert>

      <div className={css.editor_container}>
        <div className={css.editor_block}>
          <Heading size={5} text="How to respond" className={css.title} />
          <div className={css.block}>
            <InstagramRespondRadioBtns
              promoter={promoter}
              promoters={promoters}
              type="ig_live"
              customRespondToAllLabel="All comments"
              customRespondKeywordsLabel="Only to comments that contain the following keywords"
              customRespondEmojisLabel="Respond to popular emojis"
              customKeywordBroadPosition={
                screenWidth > 600 ? 'right' : 'bottom'
              }
              customKeywordBroadAlign="center"
            />
          </div>
          <div className={css.divider} />
          <div className={css.block}>
            <FrequencyCapControl
              guard={promoter}
              chatbotId={chatbot.id}
              css={{
                label: css.frequency_cap_label,
                input: css.frequency_cap_input,
                button: css.frequency_cap_button,
              }}
              customEditIcon={<EditIcon />}
            />
          </div>
        </div>

        <div className={css.content}>
          <div className={css.dialogue_editor_container}>
            <PathGraphContext.Provider value={pathGraph}>
              <Heading size={3} className={css.heading}>
                Choose or create a Dialogue
              </Heading>

              {dialogueSelectorDropdown}
              <StartBotsDialogueEditor
                chatbotId={chatbot.id}
                widgets={widgets}
                widgetsToExclude={widgetsToExclude}
                dialogueId={promoter.pageId}
                platforms={['instagram']}
                showAttributesStartWarning
                forInstagram
                disableFirstWidgetConditionalLogic
                firstWidgetNotAllowedWarning={{
                  showWarning: true,
                  message: `This widget type is NOT supported as the first message in your Instagram Live.
                   Please use a Quick Question widget instead.`,
                  startbotType: 'instagram_cg',
                }}
              />
            </PathGraphContext.Provider>
          </div>
        </div>
        {promoter.pageId && (
          <div className={css.preview}>
            <DialoguePreview
              platforms={['instagram']}
              dialogueId={promoter?.pageId}
              style={{ top: -20, marginTop: 20 }}
              sticky
            />
          </div>
        )}
      </div>
    </div>
  );
};
